import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import AdminBreadcrumb from '../components/AdminBreadcrumb';
import SovereigntyMarkers from '../components/SovereigntyMarkers';
import GoogleMapView from '../components/GoogleMapView';
import { useHousingData } from '../hooks/useHousingData';
import { PROPERTY_TYPES } from '../constants';
import { getDistance, formatDistance } from '../utils/distance';
import { geocodeLocation, geocodePropertiesParallel, syncGeocodeCacheFromD1, syncGeocodeCacheToD1, clearGeocodeCache, clearServerGeocodeCache, getGeocodeCache } from '../services/mapService';
import { geocodeGoogle } from '../services/googleMapsService';
import config from '../config';
import UNIVERSITIES from '../data/universities';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue with bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom university marker icon (larger, school-themed teardrop)
const createUniIcon = () => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="position: relative; width: 40px; height: 40px;">
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 2C11.72 2 5 8.72 5 17C5 27.75 20 38 20 38C20 38 35 27.75 35 17C35 8.72 28.28 2 20 2Z"
          fill="#8B5CF6" stroke="white" stroke-width="2"/>
        <g transform="translate(20, 17) translate(-8, -8)">
          <rect x="2" y="8" width="12" height="8" rx="1" fill="white"/>
          <path d="M8 2L14 7H2L8 2Z" fill="white"/>
          <rect x="5" y="10" width="2" height="3" rx="0.5" fill="#8B5CF6"/>
          <rect x="9" y="10" width="2" height="3" rx="0.5" fill="#8B5CF6"/>
        </g>
      </svg>
    </div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 38],
    popupAnchor: [0, -38],
  });
};

// Custom property marker (smaller green teardrop)
const createPropertyIcon = () => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="position: relative; width: 28px; height: 28px;">
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2C8.48 2 4 6.48 4 12C4 19.25 14 26 14 26C14 26 24 19.25 24 12C24 6.48 19.52 2 14 2Z"
          fill="#10B981" stroke="white" stroke-width="1.5"/>
        <circle cx="14" cy="12" r="4.5" fill="white"/>
      </svg>
    </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 26],
    popupAnchor: [0, -26],
  });
};

/**
 * Build a fallback geocoding query from displayAddress -> district+city.
 * NOTE: additionalAddresses are handled FIRST by geocodePropertiesParallel.
 * This function is only for fallback after additional addresses fail.
 */
function buildAddressQuery(property) {
  const address = property.displayAddressVi || property.displayAddress || property.addressVi || property.address || '';
  const district = property.districtVi || property.district || '';
  const city = property.cityVi || property.city || '';
  if (address && district) return `${address}, ${district}, ${city || 'Ho Chi Minh City'}`;
  if (address) return `${address}, ${city || 'Ho Chi Minh City'}`;
  if (district) return `${district}, ${city || 'Ho Chi Minh City'}`;
  return null;
}

const UniversitySearchPage = () => {
  const { t, i18n } = useTranslation();
  const isVietnamese = i18n.language === 'vi';
  const { properties } = useHousingData();
  const [selectedUniId, setSelectedUniId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [geocodingProgress, setGeocodingProgress] = useState({ total: 0, done: 0 });
  const [enrichedProperties, setEnrichedProperties] = useState([]);
  const [sortBy, setSortBy] = useState('distance');
  const [typeFilter, setTypeFilter] = useState('all');
  const [shortTermLeaseFilter, setShortTermLeaseFilter] = useState('all');
  const [searchRadius, setSearchRadius] = useState(5);
  const [mapProvider, setMapProvider] = useState('leaflet'); // 'leaflet' | 'google'
  const geocodingRef = useRef(false);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // Branch selection — when a university has multiple campuses
  const [selectedBranchIndex, setSelectedBranchIndex] = useState(0);
  const [universityCoords, setUniversityCoords] = useState(null);
  const [geocodingUni, setGeocodingUni] = useState(false);
  const [uniGeocodeFailed, setUniGeocodeFailed] = useState(false);
  const uniGeocodingRef = useRef(false);

  // Filter universities based on search query
  const filteredUniversities = useMemo(() => {
    if (!searchQuery.trim()) return UNIVERSITIES;
    const q = searchQuery.toLowerCase().trim();
    return UNIVERSITIES.filter((uni) => {
      const name = (isVietnamese ? uni.nameVi : uni.name).toLowerCase();
      const nameOther = (isVietnamese ? uni.name : uni.nameVi).toLowerCase();
      return name.includes(q) || nameOther.includes(q);
    });
  }, [searchQuery, isVietnamese]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        searchRef.current &&
        !searchRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedUniversity = useMemo(() => {
    if (!selectedUniId) return null;
    return UNIVERSITIES.find((u) => u.id === selectedUniId) || null;
  }, [selectedUniId]);

  // Resolve the active campus: HQ (index 0) or a selected branch
  const activeCampus = useMemo(() => {
    if (!selectedUniversity) return null;
    // index 0 = HQ, index >= 1 = branches[index-1]
    if (selectedBranchIndex === 0) {
      return {
        name: selectedUniversity.name,
        nameVi: selectedUniversity.nameVi,
        address: selectedUniversity.address,
        lat: selectedUniversity.lat,
        lng: selectedUniversity.lng,
      };
    }
    const branch = (selectedUniversity.branches || [])[selectedBranchIndex - 1];
    return branch || null;
  }, [selectedUniversity, selectedBranchIndex]);

  // Get all campus options for the current university (HQ + branches)
  const campusOptions = useMemo(() => {
    if (!selectedUniversity) return [];
    const options = [{
      name: selectedUniversity.name,
      nameVi: selectedUniversity.nameVi,
      address: selectedUniversity.address,
      lat: selectedUniversity.lat,
      lng: selectedUniversity.lng,
      isHQ: true,
    }];
    for (const b of selectedUniversity.branches || []) {
      options.push({ ...b, isHQ: false });
    }
    return options;
  }, [selectedUniversity]);

  // Use pre-verified coordinates — instantly set from static data (no geocoding needed)
  useEffect(() => {
    if (!selectedUniversity) {
      setUniversityCoords(null);
      setUniGeocodeFailed(false);
      setGeocodingUni(false);
      uniGeocodingRef.current = false;
      setSelectedBranchIndex(0);
      return;
    }

    // Reset branch to HQ when a new university is selected
    setSelectedBranchIndex(0);
    uniGeocodingRef.current = false;
    setGeocodingUni(false);
    setUniGeocodeFailed(false);

    // Use the pre-verified coordinates from the static dataset
    if (selectedUniversity.lat != null && selectedUniversity.lng != null) {
      setUniversityCoords({
        lat: selectedUniversity.lat,
        lng: selectedUniversity.lng,
        display_name: selectedUniversity.address,
      });
    } else {
      // Fallback: geocode dynamically if coordinates are missing
      setGeocodingUni(true);
      let cancelled = false;

      const resolveCoords = async () => {
        // Use the shared geocodeCache from mapService so results are visible
        // to syncGeocodeCacheToD1() and other consumers.
        const sharedCache = getGeocodeCache();
        // Also keep a local ref for convenience (sharedCache is the same object)
        const cache = sharedCache;

        // Check cache for this address
        const cacheKey = selectedUniversity.address.toLowerCase().trim();
        let coords = cache[cacheKey];

        if (!coords) {
          // Try geocoding via OSM first (which auto-stores in D1 on the server side),
          // fallback to Google if available (Google results only exist client-side)
          coords = await geocodeLocation(selectedUniversity.address, '');
          if (!coords && config.googleMaps?.apiKey) {
            coords = await geocodeGoogle(selectedUniversity.address);
          }
          if (coords) {
            cache[cacheKey] = coords;
          }
        }

        if (!cancelled) {
          if (coords) {
            setUniversityCoords(coords);
            setUniGeocodeFailed(false);
          } else {
            setUniversityCoords(null);
            setUniGeocodeFailed(true);
          }
          setGeocodingUni(false);
          uniGeocodingRef.current = false;
        }
      };

      resolveCoords();

      return () => {
        cancelled = true;
      };
    }
  }, [selectedUniversity]);

  // When a branch is selected, use the pre-verified branch coordinates immediately
  useEffect(() => {
    if (!activeCampus || !selectedUniversity) return;
    if (activeCampus.lat != null && activeCampus.lng != null) {
      setUniversityCoords({
        lat: activeCampus.lat,
        lng: activeCampus.lng,
        display_name: activeCampus.address,
      });
      setGeocodingUni(false);
      setUniGeocodeFailed(false);
    }
  }, [activeCampus, selectedUniversity]);

  // Identify properties that need geocoding — those without lat/lng
  const propertiesToGeocode = useMemo(() => {
    if (!selectedUniversity) return [];
    return properties.filter((p) => {
      if (p.lat && p.lng) return false;
      return !!buildAddressQuery(p);
    });
  }, [selectedUniversity, properties]);

  // Batch-geocode all properties that lack coordinates in PARALLEL when university changes
  useEffect(() => {
    if (!selectedUniversity) {
      setEnrichedProperties([]);
      geocodingRef.current = false;
      return;
    }

    // Build the enriched list: properties with existing coords
    const alreadyReady = properties
      .filter((p) => p.lat && p.lng)
      .map((p) => ({ ...p, _geocoding: false, _geocodeFailed: false }));

    // Start with already-ready properties
    setEnrichedProperties(alreadyReady);
    setGeocodingProgress({ total: propertiesToGeocode.length, done: 0 });

    if (propertiesToGeocode.length === 0) {
      geocodingRef.current = false;
      return;
    }

    // Prevent duplicate geocoding runs
    if (geocodingRef.current) return;
    geocodingRef.current = true;

    let cancelled = false;

    // Geocode all properties in parallel with concurrency control
    const runGeocoding = async () => {
      const enrichedList = await geocodePropertiesParallel(
        propertiesToGeocode,
        5, // concurrency
        (done, total) => {
          if (!cancelled) {
            setGeocodingProgress({ total, done });
          }
        }
      );

      if (!cancelled) {
        setEnrichedProperties((prev) => {
          // Merge already ready with newly geocoded
          const existingIds = new Set(prev.map((p) => p.id));
          const newOnes = enrichedList.filter((p) => !existingIds.has(p.id));
          return [...prev, ...newOnes];
        });
        geocodingRef.current = false;

        // Auto-push newly geocoded results to D1 so they persist
        // for other users/sessions. Fire-and-forget — don't block the UI.
        syncGeocodeCacheToD1()
          .then(r => {
            if (r.success && r.count > 0) {
              console.log(`[Geocode] Auto-pushed ${r.count} entries to D1 after batch geocoding`);
            }
          })
          .catch(err => console.warn('[Geocode] Auto-push to D1 failed:', err));
      }
    };

    runGeocoding();

    return () => {
      cancelled = true;
    };
  }, [selectedUniversity, properties, propertiesToGeocode]);

  // Compute properties within radius with type filter and sort
  const nearbyProperties = useMemo(() => {
    if (!selectedUniversity || !universityCoords) return [];

    let filtered = enrichedProperties
      .filter((p) => p.lat && p.lng && !p._geocodeFailed)
      .map((p) => ({
        ...p,
        distance: getDistance(universityCoords.lat, universityCoords.lng, p.lat, p.lng),
      }))
      .filter((p) => p.distance <= searchRadius);

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter((p) => {
        const propType = (p.type || '').toLowerCase().trim();
        return propType === typeFilter.toLowerCase();
      });
    }

    // Apply short term lease filter
    if (shortTermLeaseFilter === 'shortTerm') {
      filtered = filtered.filter((p) => p.shortTermLease === true);
    } else if (shortTermLeaseFilter === 'longTerm') {
      filtered = filtered.filter((p) => p.shortTermLease !== true);
    }

    // Apply sort
    if (sortBy === 'distance') {
      filtered.sort((a, b) => a.distance - b.distance);
    } else if (sortBy === 'price_asc') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price_desc') {
      filtered.sort((a, b) => b.price - a.price);
    }

    return filtered;
  }, [selectedUniversity, universityCoords, enrichedProperties, sortBy, typeFilter, searchRadius, shortTermLeaseFilter]);

  const geocodingInProgress = geocodingProgress.total > 0 && geocodingProgress.done < geocodingProgress.total;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0,
    }).format(Math.round(price));
  };

  const mapCenter = universityCoords
    ? [universityCoords.lat, universityCoords.lng]
    : [10.7769, 106.6952]; // Center of HCMC

  // Build Google Maps markers
  const googleMapMarkers = useMemo(() => {
    if (!universityCoords) return [];
    const markers = [];

    // University marker — use active campus info when a branch is selected
    markers.push({
      id: 'university',
      position: { lat: universityCoords.lat, lng: universityCoords.lng },
      title: isVietnamese ? (activeCampus?.nameVi || selectedUniversity?.nameVi) : (activeCampus?.name || selectedUniversity?.name),
      popup: `<div style="font-size:13px;min-width:180px">
        <p style="font-weight:700;color:#1f2937;margin:0 0 4px">${isVietnamese ? (activeCampus?.nameVi || selectedUniversity?.nameVi) : (activeCampus?.name || selectedUniversity?.name)}</p>
        <p style="color:#6b7280;font-size:12px;margin:0">${activeCampus?.address || selectedUniversity?.address}</p>
      </div>`,
    });

    // Property markers
    nearbyProperties.forEach((p) => {
      markers.push({
        id: p.id,
        position: { lat: p.lat, lng: p.lng },
        title: isVietnamese && p.titleVi ? p.titleVi : p.title,
        popup: `<div style="font-size:13px;min-width:200px">
          <p style="font-weight:700;color:#1f2937;margin:0 0 4px">${isVietnamese && p.titleVi ? p.titleVi : p.title}</p>
          <p style="color:#059669;font-weight:600;margin:0 0 4px">${formatPrice(p.price)}</p>
          <p style="color:#6b7280;font-size:12px;margin:0">${p.districtVi || p.district}</p>
          <p style="color:#9ca3af;font-size:11px;margin:4px 0 0">${formatDistance(p.distance)} ${t('university.fromUni')}</p>
        </div>`,
      });
    });

    return markers;
  }, [universityCoords, nearbyProperties, activeCampus, isVietnamese, selectedUniversity, t]);

  const hasGoogleApiKey = !!config.googleMaps?.apiKey;

  // D1 sync state
  const [d1SyncState, setD1SyncState] = useState({ loading: false, message: '', count: 0 });

  const handleSyncFromD1 = useCallback(async () => {
    setD1SyncState({ loading: true, message: 'Đang đồng bộ...', count: 0 });
    const result = await syncGeocodeCacheFromD1();
    if (result.success) {
      setD1SyncState({
        loading: false,
        message: result.count > 0
          ? `Đã đồng bộ ${result.count} tọa độ từ D1`
          : 'Chưa có dữ liệu tọa độ trong D1',
        count: result.count,
      });
    } else {
      setD1SyncState({
        loading: false,
        message: `Lỗi: ${result.error || 'Không thể đồng bộ'}`,
        count: 0,
      });
    }
    // Auto-clear success message after 5 seconds
    setTimeout(() => {
      setD1SyncState({ loading: false, message: '', count: 0 });
    }, 5000);
  }, []);

  const handleClearCache = useCallback(async () => {
    // Clear local client-side cache
    const localCount = clearGeocodeCache();

    // Clear server-side cache (in-memory + D1)
    const serverResult = await clearServerGeocodeCache();

    const parts = [];
    if (localCount > 0) parts.push(`${localCount} local`);
    if (serverResult.success) {
      if (serverResult.memoryCleared > 0) parts.push(`${serverResult.memoryCleared} server memory`);
      if (serverResult.d1Cleared > 0) parts.push(`${serverResult.d1Cleared} D1`);
    }

    const msg = parts.length > 0
      ? `Đã xóa cache (${parts.join(', ')})`
      : 'Cache đã trống';

    setD1SyncState({
      loading: false,
      message: msg,
      count: 0,
    });
    setTimeout(() => {
      setD1SyncState(prev => prev.message ? { loading: false, message: '', count: 0 } : prev);
    }, 4000);
  }, []);

  const handlePushToD1 = useCallback(async () => {
    setD1SyncState({ loading: true, message: 'Đang đẩy lên D1...', count: 0 });
    const result = await syncGeocodeCacheToD1();
    if (result.success) {
      setD1SyncState({
        loading: false,
        message: result.count > 0
          ? `Đã đẩy ${result.count} tọa độ lên D1`
          : 'Không có tọa độ nào để đẩy',
        count: result.count,
      });
    } else {
      setD1SyncState({
        loading: false,
        message: `Lỗi: ${result.error || 'Không thể đẩy lên D1'}`,
        count: 0,
      });
    }
    setTimeout(() => {
      setD1SyncState({ loading: false, message: '', count: 0 });
    }, 5000);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AdminBreadcrumb />
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('university.title')}</h1>
          <p className="text-gray-500 mt-2">{t('university.subtitle')}</p>
        </div>

        {/* University Autocomplete */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <label htmlFor="uni-select" className="block text-sm font-semibold text-gray-700 mb-2">
            {t('university.select')}
          </label>
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              id="uni-select"
              type="text"
              ref={searchRef}
              autoComplete="off"
              value={selectedUniId ? (isVietnamese ? UNIVERSITIES.find(u => u.id === selectedUniId)?.nameVi || '' : UNIVERSITIES.find(u => u.id === selectedUniId)?.name || '') : searchQuery}
              onChange={(e) => {
                setSelectedUniId('');
                setSearchQuery(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              placeholder={t('university.placeholder')}
              className="w-full pl-10 pr-10 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 font-medium focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
            />
            {selectedUniId && (
              <button
                onClick={() => { setSelectedUniId(''); setSearchQuery(''); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}

            {/* Dropdown */}
            {showDropdown && (
              <div
                ref={dropdownRef}
                className="absolute z-50 mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-100 max-h-72 overflow-y-auto"
              >
                {filteredUniversities.length === 0 ? (
                  <div className="px-4 py-8 text-center text-gray-400 text-sm">
                    {t('university.noResults')}
                  </div>
                ) : (
                  filteredUniversities.map((uni) => (
                    <button
                      key={uni.id}
                      onClick={() => {
                        setSelectedUniId(uni.id);
                        setSearchQuery('');
                        setShowDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-purple-50 transition-colors border-b border-gray-50 last:border-b-0 ${
                        selectedUniId === uni.id ? 'bg-purple-50' : ''
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900">
                        {isVietnamese ? uni.nameVi : uni.name}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {isVietnamese ? uni.name : uni.nameVi}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {selectedUniversity ? (
          <>
            {/* University Info Bar */}
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl p-6 mb-6 text-white shadow-md">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <h2 className="text-xl font-bold mb-1">
                    {isVietnamese ? selectedUniversity.nameVi : selectedUniversity.name}
                  </h2>
                  <p className="text-purple-100 text-sm">
                    {activeCampus ? activeCampus.address : selectedUniversity.address}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold">{nearbyProperties.length}</div>
                  <div className="text-purple-100 text-sm">{t('university.propertiesFound')}</div>
                </div>
              </div>

              {/* Campus / Branch Selector */}
              {campusOptions.length > 1 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {campusOptions.map((opt, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedBranchIndex(idx)}
                      className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors border ${
                        selectedBranchIndex === idx
                          ? 'bg-white text-purple-700 border-white shadow-sm'
                          : 'bg-purple-500/30 text-purple-100 border-purple-300/40 hover:bg-purple-400/30'
                      }`}
                    >
                      {isVietnamese ? opt.nameVi : opt.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Radius Slider */}
            {universityCoords && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-700">Bán kính tìm kiếm</label>
                  <span className="text-sm font-bold text-purple-600 bg-purple-50 px-3 py-1 rounded-full">{searchRadius} km</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSearchRadius((r) => Math.max(1, r - 1))}
                    disabled={searchRadius <= 1}
                    className="w-8 h-8 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-700 font-bold flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-lg"
                  >
                    −
                  </button>
                  <input
                    type="range"
                    min="1"
                    max="50"
                    value={searchRadius}
                    onChange={(e) => setSearchRadius(parseInt(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                  />
                  <button
                    onClick={() => setSearchRadius((r) => Math.min(50, r + 1))}
                    disabled={searchRadius >= 50}
                    className="w-8 h-8 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-700 font-bold flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-lg"
                  >
                    +
                  </button>
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1 km</span>
                  <span>50 km</span>
                </div>
              </div>
            )}

            {/* Geocoding Progress Banner */}
            {(geocodingUni || geocodingInProgress) && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6 flex items-center space-x-3">
                <div className="w-5 h-5 border-2 border-blue-400 border-t-blue-600 rounded-full animate-spin flex-shrink-0" />
                <div className="flex-1">
                  {geocodingUni ? (
                    <p className="text-sm font-medium text-blue-800">
                      {t('university.geocodingUni')}
                    </p>
                  ) : (
                    <p className="text-sm font-medium text-blue-800">
                      {t('university.geocoding', { done: geocodingProgress.done, total: geocodingProgress.total })}
                    </p>
                  )}
                  {!geocodingUni && (
                    <div className="mt-2 h-1.5 bg-blue-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                        style={{ width: `${(geocodingProgress.done / geocodingProgress.total) * 100}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* University Geocode Failed */}
            {uniGeocodeFailed && !geocodingUni && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6">
                <p className="text-sm font-medium text-red-700">
                  {t('university.geocodeFailedUni')}
                </p>
                <p className="text-sm text-red-500 mt-1">{t('university.tryAnother')}</p>
              </div>
            )}

            {/* Map Toggle + D1 Sync + Clear Cache Buttons */}
            <div className="flex justify-end mb-2 gap-2 flex-wrap">
              {/* Clear Cache Button */}
              <button
                onClick={handleClearCache}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border shadow-sm
                  bg-white border-gray-200 text-gray-700 hover:bg-red-50 hover:border-red-200 hover:text-red-700"
                title="Xóa bộ nhớ tạm tọa độ (sẽ tải lại từ server)"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>Clear</span>
              </button>

              {/* Push to D1 Button */}
              <button
                onClick={handlePushToD1}
                disabled={d1SyncState.loading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border shadow-sm
                  bg-white border-gray-200 text-gray-700 hover:bg-green-50 hover:border-green-200 hover:text-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Đẩy tọa độ từ bộ nhớ tạm lên D1"
              >
                {d1SyncState.loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-green-400 border-t-green-600 rounded-full animate-spin" />
                    <span>Đang đẩy...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0l-4 4m4-4v12" />
                    </svg>
                    <span>Push D1</span>
                  </>
                )}
              </button>

              {/* D1 Sync Button */}
              <button
                onClick={handleSyncFromD1}
                disabled={d1SyncState.loading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border shadow-sm
                  bg-white border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Đồng bộ tọa độ đã lưu từ D1 vào bộ nhớ tạm"
              >
                {d1SyncState.loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-blue-400 border-t-blue-600 rounded-full animate-spin" />
                    <span>Đang đồng bộ...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Sync D1</span>
                  </>
                )}
              </button>

              {d1SyncState.message && (
                <span className={`inline-flex items-center px-3 py-2 rounded-lg text-xs font-medium ${
                  d1SyncState.count > 0
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : d1SyncState.message.includes('Lỗi')
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                }`}>
                  {d1SyncState.message}
                </span>
              )}

              {/* Map toggle — only when Google API key is available */}
              {universityCoords && hasGoogleApiKey && (
                <button
                  onClick={() => setMapProvider((prev) => (prev === 'leaflet' ? 'google' : 'leaflet'))}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border shadow-sm
                    bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                >
                  {mapProvider === 'leaflet' ? (
                    <>
                      <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                      </svg>
                      <span>{t('map.switchToGoogle')}</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                      </svg>
                      <span>{t('map.switchToOSM')}</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Map */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
              <div className="h-[450px]">
                {universityCoords ? (
                  mapProvider === 'leaflet' ? (
                    /* OpenStreetMap (Leaflet) */
                    <MapContainer
                      key={`uni-leaflet-${selectedUniId}-${selectedBranchIndex}-${searchRadius}`}
                      center={mapCenter}
                      zoom={13}
                      style={{ height: '100%', width: '100%' }}
                      scrollWheelZoom={true}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />

                      {/* Hoàng Sa, Trường Sa sovereignty markers */}
                      <SovereigntyMarkers />

                      {/* Radius circle */}
                      <Circle
                        center={[universityCoords.lat, universityCoords.lng]}
                        radius={searchRadius * 1000}
                        pathOptions={{
                          color: '#8B5CF6',
                          fillColor: '#8B5CF6',
                          fillOpacity: 0.08,
                          weight: 2,
                          dashArray: '8, 8',
                        }}
                      />

                      {/* University Marker */}
                      <Marker
                        position={[universityCoords.lat, universityCoords.lng]}
                        icon={createUniIcon()}
                      >
                        <Popup>
                          <div className="text-sm min-w-[180px]">
                            <p className="font-bold text-gray-900">
                              {isVietnamese ? (activeCampus?.nameVi || selectedUniversity.nameVi) : (activeCampus?.name || selectedUniversity.name)}
                            </p>
                            <p className="text-gray-500 text-xs mt-1">{activeCampus?.address || selectedUniversity.address}</p>
                          </div>
                        </Popup>
                      </Marker>

                      {/* Property Markers */}
                      {nearbyProperties.map((p) => (
                        <Marker
                          key={p.id}
                          position={[p.lat, p.lng]}
                          icon={createPropertyIcon()}
                        >
                          <Popup>
                            <div className="text-sm min-w-[200px]">
                              <p className="font-bold text-gray-900">
                                {isVietnamese && p.titleVi ? p.titleVi : p.title}
                              </p>
                              <p className="text-primary-600 font-semibold mt-1">{formatPrice(p.price)}</p>
                              <p className="text-gray-500 text-xs mt-1 flex items-center space-x-1">
                                <svg className="w-3 h-3 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span>{p.districtVi || p.district}</span>
                              </p>
                              <p className="text-gray-400 text-xs mt-1">{formatDistance(p.distance)} {t('university.fromUni')}</p>
                              <a
                                href={`/housing/${p.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 inline-block text-xs font-medium text-primary-600 hover:text-primary-700"
                              >
                                {t('university.viewDetail')} →
                              </a>
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    </MapContainer>
                  ) : (
                    /* Google Maps */
                    <GoogleMapView
                      center={{ lat: universityCoords.lat, lng: universityCoords.lng }}
                      zoom={13}
                      markers={googleMapMarkers}
                      circle={{
                        center: { lat: universityCoords.lat, lng: universityCoords.lng },
                        radius: searchRadius * 1000,
                      }}
                      height="450px"
                    />
                  )
                ) : (
                  <div className="h-full flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                      <div className="w-10 h-10 border-3 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4" />
                      <p className="text-gray-500 text-sm">{t('university.loadingMap')}</p>
                    </div>
                  </div>
                )}
              </div>
              {/* Legend */}
              <div className="flex items-center gap-4 px-4 py-3 border-t border-gray-100 bg-gray-50 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 rounded-full bg-purple-500 border-2 border-white shadow-sm" />
                  <span className="text-gray-600">{t('university.university')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white shadow-sm" />
                  <span className="text-gray-600">{t('university.property')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-0.5 border-t-2 border-dashed border-purple-400" />
                  <span className="text-gray-600">{t('university.searchRadius', { km: searchRadius })}</span>
                </div>
              </div>
            </div>

            {/* Sort & Filter Controls */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Type Filter */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('university.filterType')}</label>
                  <div className="relative">
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="w-full appearance-none bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all"
                    >
                      <option value="all">{t('university.allTypes')}</option>
                      {PROPERTY_TYPES.map((pt) => (
                        <option key={pt.value} value={pt.value}>{t(pt.labelKey)}</option>
                      ))}
                    </select>
                    <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Short Term Lease Filter */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('admin.shortTermLease')}</label>
                  <div className="relative">
                    <select
                      value={shortTermLeaseFilter}
                      onChange={(e) => setShortTermLeaseFilter(e.target.value)}
                      className="w-full appearance-none bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all"
                    >
                      <option value="all">Tất cả</option>
                      <option value="shortTerm">Cho thuê ngắn ngày</option>
                      <option value="longTerm">Cho thuê dài hạn</option>
                    </select>
                    <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Sort */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('university.sortBy')}</label>
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full appearance-none bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all"
                    >
                      <option value="distance">{t('university.sortDistance')}</option>
                      <option value="price_asc">{t('university.sortPriceAsc')}</option>
                      <option value="price_desc">{t('university.sortPriceDesc')}</option>
                    </select>
                    <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Property List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">
                  {t('university.nearbyProperties')}
                  <span className="text-gray-400 font-normal ml-2">({nearbyProperties.length})</span>
                </h3>
              </div>

              {nearbyProperties.length === 0 && !geocodingInProgress && !geocodingUni ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                  <div className="text-4xl mb-4">🏫</div>
                  <p className="text-gray-500 font-medium">{t('university.noProperties')}</p>
                  <p className="text-gray-400 text-sm mt-1">{t('university.tryAnother')}</p>
                </div>
              ) : nearbyProperties.length === 0 && (geocodingInProgress || geocodingUni) ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                  <div className="w-10 h-10 border-3 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-gray-500">{t('common.loading')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {nearbyProperties.map((p) => (
                    <div
                      key={p.id}
                      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow group"
                    >
                      <a
                        href={`/housing/${p.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {/* Image */}
                        <div className="relative h-40 overflow-hidden">
                          <img
                            src={p.images[0]}
                            alt={isVietnamese && p.titleVi ? p.titleVi : p.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                          {/* Distance Badge */}
                          <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-gray-700 px-2.5 py-1 rounded-lg text-xs font-semibold shadow-sm flex items-center space-x-1">
                            <svg className="w-3 h-3 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            </svg>
                            <span>{formatDistance(p.distance)}</span>
                          </div>
                          {/* Price Badge */}
                          <div className="absolute bottom-3 left-3 bg-primary-500 text-white px-2.5 py-1 rounded-lg text-xs font-semibold shadow-lg">
                            {formatPrice(p.price)}
                          </div>
                        </div>
                      </a>

                      {/* Content */}
                      <div className="p-4">
                        <a
                          href={`/housing/${p.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <h4 className="font-semibold text-gray-900 text-sm mb-1.5 line-clamp-1 group-hover:text-primary-600 transition-colors">
                            {isVietnamese && p.titleVi ? p.titleVi : p.title}
                          </h4>
                        </a>
                        <p className="text-gray-500 text-xs mb-2 line-clamp-1">
                          {p.districtVi || p.district}, {p.cityVi || p.city}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1 text-gray-400 text-xs">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>{p.amenities.length} {t('detail.amenities')}</span>
                          </div>
                          <a
                            href={`/housing/${p.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
                          >
                            {t('university.viewDetail')} →
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          /* Empty state when no university selected */
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
            <div className="text-6xl mb-6">🎓</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('university.choosePrompt')}</h2>
            <p className="text-gray-500 max-w-md mx-auto mb-8">{t('university.chooseDesc')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-2xl mx-auto">
              {UNIVERSITIES.slice(0, 20).map((uni) => (
                <button
                  key={uni.id}
                  onClick={() => setSelectedUniId(uni.id)}
                  className="bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-xl px-4 py-3 text-sm font-medium transition-colors border border-purple-200 hover:border-purple-300 text-left"
                >
                  <div className="font-medium truncate">
                    {isVietnamese ? uni.nameVi : uni.name}
                  </div>
                  <div className="text-xs text-purple-400 truncate mt-0.5">
                    {uni.address}
                  </div>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-4">{t('university.seeAll')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UniversitySearchPage;